import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  EntitySlideout,
  saveErrorReason,
  saveErrorToast,
  type BatchAction,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useClientContacts, useContactMutations } from "./api";
import { ContactForm } from "./ContactForm/ContactForm";
import { emptyContactForm } from "./types";
import type { Contact, ContactFormValues } from "./types";

export interface ContactsTabProps {
  clientId: string;
}

const isValidContact = (v: ContactFormValues): boolean => v.name.trim() !== "";

const toFormValues = (c: Contact): ContactFormValues => ({
  name: c.name,
  title: c.title ?? "",
  email: c.email ?? "",
  phone: c.phone ?? "",
});

export const ContactsTab = ({ clientId }: ContactsTabProps): JSX.Element => {
  const { rows, isLoading, isFetching, refetch } = useClientContacts(clientId);
  const { create, update, archive } = useContactMutations(clientId);
  const sel = useBatchSelection([]);

  const [view, setView] = useState<
    { mode: "list" } | { mode: "form"; editing?: Contact }
  >({ mode: "list" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const selectedContacts = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<Contact>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => row.original.title ?? "",
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => row.original.email ?? "",
    },
    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ row }) => row.original.phone ?? "",
    },
  ];

  const saveContact = async (values: ContactFormValues) => {
    try {
      if (view.mode === "form" && view.editing) {
        await update(view.editing.id, values);
      } else {
        await create(values);
      }
      setView({ mode: "list" });
      refetch();
    } catch (err) {
      setToast(saveErrorToast(err, "contact"));
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    const targets = [...selectedContacts];
    const failures: BatchFailure[] = [];
    for (const c of targets) {
      try {
        await archive(c.id);
      } catch (err) {
        failures.push({ label: c.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: `${targets.length} contact(s) archived.` }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} contact(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setConfirmOpen(true),
      variant: "danger",
    },
  ];

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Contacts"
        bare
        isEmpty={rows.length === 0}
        emptyMessage="No contacts found."
        onNew={() => setView({ mode: "form" })}
        newButtonLabel="+ Add A Contact"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(contactId) => {
            const row = rows.find((r) => r.id === String(contactId));
            if (row) setView({ mode: "form", editing: row });
          }}
          emptyMessage="No contacts found."
        />
      </AdminListCard>

      <EntitySlideout
        isOpen={view.mode === "form"}
        initialValues={
          view.mode === "form" && view.editing
            ? toFormValues(view.editing)
            : emptyContactForm
        }
        title={
          view.mode === "form" && view.editing
            ? "Edit Contact"
            : "Add a Contact"
        }
        isValid={isValidContact}
        onClose={() => setView({ mode: "list" })}
        onSave={saveContact}
      >
        {(values, onChange) => (
          <ContactForm values={values} onChange={onChange} />
        )}
      </EntitySlideout>

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Archive contacts?"
        message={`Are you sure you want to archive ${sel.selectedIds.length} contact(s)?`}
        confirmLabel="Archive"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ContactsTab;
